/**
 * Inventory Report Printing Utility
 * Generates formatted HTML for printing inventory reports to PDF
 * Follows the same pattern as quotationPrint.js
 */
import { showAlert } from "@/plugins/alertDialog";

const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return (
    "P " +
    num.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  return Number(num).toLocaleString("en-PH");
};

const getBaseStyles = () => `
  @media print {
    @page {
      margin: 0.3in 0.4in;
      size: landscape;
    }
    body { margin: 0; }
    .no-print { display: none !important; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; }
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    padding: 12px 16px;
    color: #2c3e50;
    font-size: 9px;
    line-height: 1.3;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 6px;
    border-bottom: 3px double #1976d2;
    margin-bottom: 8px;
  }
  .company-name { font-size: 18px; font-weight: bold; letter-spacing: 1.5px; }
  .company-name .red { color: #d32f2f; }
  .company-name .blue { color: #1976d2; }
  .company-tagline { font-size: 8px; color: #555; font-weight: 600; }
  .company-details { font-size: 7px; color: #777; margin-top: 1px; }
  .doc-title { font-size: 16px; font-weight: 700; color: #1976d2; letter-spacing: 1px; text-align: right; }
  .doc-subtitle { font-size: 9px; color: #555; text-align: right; margin-top: 2px; }

  .filters-bar {
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    padding: 4px 10px;
    margin-bottom: 8px;
    font-size: 8px;
    color: #555;
  }
  .filters-bar span { margin-right: 15px; }
  .filters-bar strong { color: #333; }

  .summary-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }
  .summary-card {
    flex: 1;
    border: 1px solid #ccc;
    padding: 6px 10px;
    text-align: center;
  }
  .summary-card .value { font-size: 13px; font-weight: 700; }
  .summary-card .label { font-size: 7px; color: #666; text-transform: uppercase; letter-spacing: 0.3px; }

  table.report-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 8px;
  }
  table.report-table th {
    background: #1976d2;
    color: white;
    padding: 4px 5px;
    font-size: 8px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    text-align: left;
    white-space: nowrap;
  }
  table.report-table td {
    border: 1px solid #e0e0e0;
    padding: 3px 5px;
    font-size: 9px;
  }
  table.report-table tbody tr:nth-child(even) { background: #f9f9f9; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .bold { font-weight: 700; }

  .group-header {
    background: #1565c0 !important;
    color: white;
    font-weight: 700;
    font-size: 9px;
  }
  .group-header td { border-color: #1565c0; color: white; }

  .sub-type-header {
    background: #e3f2fd !important;
    font-weight: 700;
    font-size: 9px;
  }
  .sub-type-header td {
    border-color: #bbdefb;
    color: #1565c0;
    padding: 4px 5px;
  }

  .category-banner {
    background: #1565c0;
    color: white;
    padding: 6px 10px;
    margin: 14px 0 0 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    page-break-inside: avoid;
  }
  .category-banner .cat-stats {
    font-weight: 400;
    font-size: 8px;
    opacity: 0.9;
  }
  .category-banner-sub {
    display: flex;
    gap: 12px;
    font-size: 8px;
    font-weight: 400;
    opacity: 0.85;
  }

  .footer {
    margin-top: 10px;
    padding-top: 4px;
    border-top: 1px solid #ddd;
    text-align: center;
    color: #aaa;
    font-size: 7px;
  }
`;

const generateHeader = (reportTitle) => `
  <div class="header">
    <div>
      <div class="company-name">
        <span class="red">JMB</span> <span class="blue">COMPUTERS</span>
      </div>
      <div class="company-tagline">JMB COMPUTER AND LAPTOP REPAIR SERVICES</div>
      <div class="company-details">VAT REG | TIN: 703-730-173 | PhilGEPS Registered Platinum</div>
      <div class="company-details">Main: P2, Magosilom, Cantilan, Surigao del Sur | Tandag: P Villa Bali, Mabua, Tandag City</div>
    </div>
    <div>
      <div class="doc-title">${reportTitle}</div>
      <div class="doc-subtitle">Inventory Report</div>
    </div>
  </div>
`;

const generateFiltersBar = (filters) => {
  const parts = [];
  if (filters.from_date)
    parts.push(`<span><strong>From:</strong> ${filters.from_date}</span>`);
  if (filters.to_date)
    parts.push(`<span><strong>To:</strong> ${filters.to_date}</span>`);
  if (filters.item_type_id)
    parts.push(
      `<span><strong>Type:</strong> ${
        filters.item_type_name || filters.item_type_id
      }</span>`
    );
  if (filters.supplier)
    parts.push(`<span><strong>Supplier:</strong> ${filters.supplier}</span>`);
  if (filters.sort_by) {
    const sortLabels = {
      item_name: "Item Name",
      item_type: "Item Type",
      supplier: "Supplier",
      quantity: "Quantity",
      unit_price: "Unit Cost",
      selling_price: "Selling Price",
    };
    const label = sortLabels[filters.sort_by] || filters.sort_by;
    const dir = filters.sort_dir === "desc" ? "Descending" : "Ascending";
    parts.push(`<span><strong>Sorted by:</strong> ${label} (${dir})</span>`);
  }
  if (parts.length === 0) return "";
  return `<div class="filters-bar">${parts.join("")}</div>`;
};

const sortItems = (items, sortBy, sortDir) => {
  if (!sortBy || !items || items.length === 0) return items;
  const dir = sortDir === "desc" ? -1 : 1;
  return [...items].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (aVal === null || aVal === undefined) aVal = "";
    if (bVal === null || bVal === undefined) bVal = "";
    if (typeof aVal === "number" && typeof bVal === "number") {
      return (aVal - bVal) * dir;
    }
    return String(aVal).localeCompare(String(bVal)) * dir;
  });
};

const generateSummaryCards = (cards) => {
  if (!cards || cards.length === 0) return "";
  return `<div class="summary-row">${cards
    .map(
      (c) =>
        `<div class="summary-card"><div class="value">${c.value}</div><div class="label">${c.label}</div></div>`
    )
    .join("")}</div>`;
};

const generateTable = (headers, rows) => {
  if (!rows || rows.length === 0) {
    return `<p style="text-align:center;color:#999;padding:20px;">No data available.</p>`;
  }
  let html = `<table class="report-table"><thead><tr>`;
  headers.forEach((h) => {
    const align = h.align ? ` style="text-align:${h.align};"` : "";
    html += `<th${align}>${h.text}</th>`;
  });
  html += `</tr></thead><tbody>`;
  rows.forEach((row) => {
    if (row._isGroupHeader) {
      html += `<tr class="group-header"><td colspan="${headers.length}">${row._groupLabel}</td></tr>`;
      return;
    }
    html += `<tr>`;
    headers.forEach((h) => {
      const align = h.align ? ` style="text-align:${h.align};"` : "";
      const cls = h.bold ? ' class="bold"' : "";
      html += `<td${align}${cls}>${
        row[h.key] !== undefined && row[h.key] !== null ? row[h.key] : "-"
      }</td>`;
    });
    html += `</tr>`;
  });
  html += `</tbody></table>`;
  return html;
};

const generateFooter = () =>
  `<div class="footer">Generated on ${new Date().toLocaleString()} | JMB Computer and Laptop Repair Services</div>`;

const wrapHTML = (title, bodyContent) => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>${getBaseStyles()}</style>
</head>
<body>${bodyContent}</body>
</html>`;

// ─── Report Generators ──────────────────────────────────────────────────

const generateLowStockHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Low Stock Items", value: report.total_count },
    {
      label: "Inventory Value at Risk",
      value: formatCurrency(report.total_inventory_at_risk),
    },
    { label: "Generated At", value: report.generated_at },
  ]);
  const headers = [
    { text: "Item Name", key: "item_name" },
    { text: "Type", key: "item_type" },
    { text: "Supplier", key: "supplier" },
    { text: "Batch", key: "batch_name" },
    { text: "Current Qty", key: "quantity", align: "center" },
    { text: "Reorder Pt", key: "reorder_point", align: "center" },
    { text: "Min Stock", key: "min_stock_level", align: "center" },
    { text: "Deficit", key: "deficit", align: "center" },
    { text: "Severity", key: "severity_pct", align: "center" },
    { text: "Unit Cost", key: "unit_price", align: "right" },
    { text: "Selling Price", key: "selling_price", align: "right" },
    { text: "Inv. Value", key: "inventory_value", align: "right" },
    { text: "Branch", key: "branch_name" },
  ];
  const sortedItems = sortItems(
    report.items || [],
    filters.sort_by,
    filters.sort_dir
  );
  const rows = sortedItems.map((i) => ({
    ...i,
    severity_pct: i.severity_pct + "%",
    unit_price: formatCurrency(i.unit_price),
    selling_price: formatCurrency(i.selling_price),
    inventory_value: formatCurrency(i.inventory_value),
    branch_name: i.branch ? i.branch.branch_name : "-",
  }));
  const body =
    generateHeader("LOW STOCK REPORT") +
    generateFiltersBar(filters) +
    summary +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Low Stock Report", body);
};

const generateOutOfStockHTML = (report, filters) => {
  const criticalCount = (report.items || []).filter(
    (i) => i.days_out_of_stock !== null && i.days_out_of_stock >= 30
  ).length;
  const summary = generateSummaryCards([
    { label: "Out of Stock Items", value: report.total_count },
    { label: "Out 30+ Days", value: criticalCount },
    { label: "Generated At", value: report.generated_at },
  ]);
  const headers = [
    { text: "Item Name", key: "item_name" },
    { text: "Type", key: "item_type" },
    { text: "Supplier", key: "supplier" },
    { text: "Batch", key: "batch_name" },
    { text: "Current Qty", key: "quantity", align: "center" },
    { text: "Last Sold", key: "last_sold_date" },
    { text: "Days Out", key: "days_out_of_stock", align: "center" },
    { text: "Reorder Pt", key: "reorder_point", align: "center" },
    { text: "Unit Cost", key: "unit_price", align: "right" },
    { text: "Selling Price", key: "selling_price", align: "right" },
    { text: "Branch", key: "branch_name" },
  ];
  const sortedItems = sortItems(
    report.items || [],
    filters.sort_by,
    filters.sort_dir
  );
  const rows = sortedItems.map((i) => ({
    ...i,
    days_out_of_stock:
      i.days_out_of_stock !== null ? i.days_out_of_stock + " days" : "N/A",
    unit_price: formatCurrency(i.unit_price),
    selling_price: formatCurrency(i.selling_price),
    branch_name: i.branch ? i.branch.branch_name : "-",
  }));
  const body =
    generateHeader("OUT OF STOCK REPORT") +
    generateFiltersBar(filters) +
    summary +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Out of Stock Report", body);
};

const generateFastestMovingHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Items with Sales", value: report.total_count },
    {
      label: "Total Units Sold",
      value: formatNumber(report.grand_total_qty_sold),
    },
    {
      label: "Total Revenue",
      value: formatCurrency(report.grand_total_revenue),
    },
    {
      label: "Period",
      value:
        report.period_days +
        " days (" +
        report.from_date +
        " - " +
        report.to_date +
        ")",
    },
  ]);
  const headers = [
    { text: "#", key: "rank", align: "center" },
    { text: "Item Name", key: "item_name" },
    { text: "Type", key: "item_type" },
    { text: "Supplier", key: "supplier" },
    { text: "Batch", key: "batch_name" },
    { text: "Qty Sold", key: "total_qty_sold", align: "center" },
    { text: "Revenue", key: "total_revenue", align: "right" },
    { text: "Txns", key: "transaction_count", align: "center" },
    { text: "Avg Daily", key: "avg_daily_sales", align: "center" },
    { text: "Stock", key: "current_stock", align: "center" },
    { text: "Days to Stockout", key: "days_until_stockout", align: "center" },
    { text: "Unit Cost", key: "unit_price", align: "right" },
    { text: "Selling Price", key: "selling_price", align: "right" },
  ];
  const sortedItems = sortItems(
    report.items || [],
    filters.sort_by,
    filters.sort_dir
  );
  const rows = sortedItems.map((i, idx) => ({
    ...i,
    rank: i.rank || idx + 1,
    total_revenue: formatCurrency(i.total_revenue),
    days_until_stockout:
      i.days_until_stockout >= 999 ? "999+" : i.days_until_stockout,
    unit_price: formatCurrency(i.unit_price),
    selling_price: formatCurrency(i.selling_price),
  }));
  const body =
    generateHeader("FASTEST MOVING ITEMS") +
    generateFiltersBar(filters) +
    summary +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Fastest Moving Report", body);
};

const generateSlowMovingHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Slow/Dead Stock Items", value: report.total_count },
    {
      label: "Capital Tied Up",
      value: formatCurrency(report.total_cost_tied_up),
    },
    { label: "Threshold", value: report.days_threshold + " days" },
  ]);
  const headers = [
    { text: "Item Name", key: "item_name" },
    { text: "Type", key: "item_type" },
    { text: "Supplier", key: "supplier" },
    { text: "Batch", key: "batch_name" },
    { text: "Stock on Hand", key: "quantity", align: "center" },
    { text: "Last Sold", key: "last_sold_date" },
    { text: "Days Since Sale", key: "days_since_last_sale", align: "center" },
    { text: "Unit Cost", key: "unit_price", align: "right" },
    { text: "Selling Price", key: "selling_price", align: "right" },
    { text: "Cost Tied Up", key: "inventory_cost_tied_up", align: "right" },
    { text: "Branch", key: "branch_name" },
  ];
  const sortedItems = sortItems(
    report.items || [],
    filters.sort_by,
    filters.sort_dir
  );
  const rows = sortedItems.map((i) => ({
    ...i,
    days_since_last_sale:
      i.days_since_last_sale !== null
        ? i.days_since_last_sale + " days"
        : "Never",
    unit_price: formatCurrency(i.unit_price),
    selling_price: formatCurrency(i.selling_price),
    inventory_cost_tied_up: formatCurrency(i.inventory_cost_tied_up),
    branch_name: i.branch ? i.branch.branch_name : "-",
  }));
  const body =
    generateHeader("SLOW MOVING ITEMS") +
    generateFiltersBar(filters) +
    summary +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Slow Moving Report", body);
};

const generateStockMovementHTML = (report, filters) => {
  const t = report.totals || {};
  const summary = generateSummaryCards([
    { label: "Items", value: report.total_item_count },
    { label: "Total Sold", value: formatNumber(t.total_sold) },
    { label: "Total Consumed", value: formatNumber(t.total_consumed) },
    { label: "Transferred Out", value: formatNumber(t.total_transferred_out) },
    { label: "Transferred In", value: formatNumber(t.total_transferred_in) },
    { label: "Returned", value: formatNumber(t.total_returned) },
  ]);
  const periodInfo = `<div class="filters-bar"><strong>Period:</strong> ${report.from_date} to ${report.to_date}</div>`;
  const headers = [
    { text: "Item Name", key: "item_name" },
    { text: "Supplier", key: "supplier" },
    { text: "Batch", key: "batch_name" },
    { text: "Opening", key: "opening_stock", align: "center" },
    { text: "Sold", key: "sold", align: "center" },
    { text: "Consumed", key: "consumed", align: "center" },
    { text: "Xfer Out", key: "transferred_out", align: "center" },
    { text: "Xfer In", key: "transferred_in", align: "center" },
    { text: "Returned", key: "returned", align: "center" },
    { text: "Closing", key: "closing_stock", align: "center" },
    { text: "Net Change", key: "net_change", align: "center" },
  ];
  const rows = [];
  // Flatten all group items for sorting
  let allItems = [];
  (report.groups || []).forEach((group) => {
    (group.items || []).forEach((i) => {
      allItems.push({
        ...i,
        _group_type: group.item_type || "Uncategorized",
        _group_sold: group.subtotal_sold || 0,
        _group_consumed: group.subtotal_consumed || 0,
      });
    });
  });
  allItems = sortItems(allItems, filters.sort_by, filters.sort_dir);
  // Re-group after sorting if sort_by is item_type or supplier, otherwise flat
  if (filters.sort_by === "item_type" || !filters.sort_by) {
    const grouped = {};
    allItems.forEach((i) => {
      const key = i._group_type;
      if (!grouped[key])
        grouped[key] = {
          items: [],
          sold: i._group_sold,
          consumed: i._group_consumed,
        };
      grouped[key].items.push(i);
    });
    Object.keys(grouped)
      .sort((a, b) =>
        filters.sort_dir === "desc" ? b.localeCompare(a) : a.localeCompare(b)
      )
      .forEach((key) => {
        rows.push({
          _isGroupHeader: true,
          _groupLabel:
            key +
            "  |  Sold: " +
            grouped[key].sold +
            "  |  Consumed: " +
            grouped[key].consumed,
        });
        grouped[key].items.forEach((i) => {
          rows.push({
            ...i,
            sold: i.sold > 0 ? "-" + i.sold : "0",
            consumed: i.consumed > 0 ? "-" + i.consumed : "0",
            transferred_out:
              i.transferred_out > 0 ? "-" + i.transferred_out : "0",
            transferred_in: i.transferred_in > 0 ? "+" + i.transferred_in : "0",
            returned: i.returned > 0 ? "-" + i.returned : "0",
            net_change:
              i.net_change > 0 ? "+" + i.net_change : String(i.net_change),
          });
        });
      });
  } else {
    allItems.forEach((i) => {
      rows.push({
        ...i,
        sold: i.sold > 0 ? "-" + i.sold : "0",
        consumed: i.consumed > 0 ? "-" + i.consumed : "0",
        transferred_out: i.transferred_out > 0 ? "-" + i.transferred_out : "0",
        transferred_in: i.transferred_in > 0 ? "+" + i.transferred_in : "0",
        returned: i.returned > 0 ? "-" + i.returned : "0",
        net_change:
          i.net_change > 0 ? "+" + i.net_change : String(i.net_change),
      });
    });
  }
  const body =
    generateHeader("STOCK MOVEMENT REPORT") +
    generateFiltersBar(filters) +
    summary +
    periodInfo +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Stock Movement Report", body);
};

const generateTurnoverHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Period Turnover", value: report.overall_turnover_ratio + "x" },
    {
      label: "Annualized Turnover",
      value: report.overall_annualized_turnover + "x",
    },
    {
      label: "Total Revenue",
      value: formatCurrency(report.grand_total_revenue),
    },
    {
      label: "Gross Profit",
      value: formatCurrency(report.grand_total_gross_profit),
    },
  ]);
  const secondSummary = generateSummaryCards([
    {
      label: "Cost of Goods Sold",
      value: formatCurrency(report.grand_total_cogs),
    },
    {
      label: "Avg Inventory Value",
      value: formatCurrency(report.grand_total_avg_inventory),
    },
    {
      label: "Period",
      value:
        report.period_days +
        " days (" +
        report.from_date +
        " - " +
        report.to_date +
        ")",
    },
  ]);
  const headers = [
    { text: "Item Name", key: "item_name" },
    { text: "Supplier", key: "supplier" },
    { text: "Batch", key: "batch_name" },
    { text: "Stock", key: "current_stock", align: "center" },
    { text: "Qty Sold", key: "qty_sold", align: "center" },
    { text: "COGS", key: "cogs", align: "right" },
    { text: "Revenue", key: "revenue", align: "right" },
    { text: "Gross Profit", key: "gross_profit", align: "right" },
    { text: "Margin %", key: "gross_margin_pct", align: "center" },
    { text: "Inv. Value", key: "current_inventory_value", align: "right" },
    { text: "Turnover", key: "turnover_ratio", align: "center" },
    { text: "Annual", key: "annualized_turnover", align: "center" },
    { text: "Days to Sell", key: "days_to_sell", align: "center" },
  ];
  const rows = [];
  let allItems = [];
  (report.groups || []).forEach((group) => {
    (group.items || []).forEach((i) => {
      allItems.push({
        ...i,
        _group_type: group.item_type || "Uncategorized",
        _group_cogs: group.subtotal_cogs,
        _group_revenue: group.subtotal_revenue,
        _group_turnover: group.group_turnover_ratio || 0,
      });
    });
  });
  allItems = sortItems(allItems, filters.sort_by, filters.sort_dir);
  if (filters.sort_by === "item_type" || !filters.sort_by) {
    const grouped = {};
    allItems.forEach((i) => {
      const key = i._group_type;
      if (!grouped[key])
        grouped[key] = {
          items: [],
          cogs: i._group_cogs,
          revenue: i._group_revenue,
          turnover: i._group_turnover,
        };
      grouped[key].items.push(i);
    });
    Object.keys(grouped)
      .sort((a, b) =>
        filters.sort_dir === "desc" ? b.localeCompare(a) : a.localeCompare(b)
      )
      .forEach((key) => {
        const g = grouped[key];
        rows.push({
          _isGroupHeader: true,
          _groupLabel:
            key +
            "  |  COGS: " +
            formatCurrency(g.cogs) +
            "  |  Revenue: " +
            formatCurrency(g.revenue) +
            "  |  Turnover: " +
            g.turnover +
            "x",
        });
        g.items.forEach((i) => {
          rows.push({
            ...i,
            cogs: formatCurrency(i.cogs),
            revenue: formatCurrency(i.revenue),
            gross_profit: formatCurrency(i.gross_profit),
            gross_margin_pct: i.gross_margin_pct + "%",
            current_inventory_value: formatCurrency(i.current_inventory_value),
            turnover_ratio: i.turnover_ratio + "x",
            annualized_turnover: i.annualized_turnover + "x",
            days_to_sell: i.days_to_sell >= 999 ? "999+" : i.days_to_sell,
          });
        });
      });
  } else {
    allItems.forEach((i) => {
      rows.push({
        ...i,
        cogs: formatCurrency(i.cogs),
        revenue: formatCurrency(i.revenue),
        gross_profit: formatCurrency(i.gross_profit),
        gross_margin_pct: i.gross_margin_pct + "%",
        current_inventory_value: formatCurrency(i.current_inventory_value),
        turnover_ratio: i.turnover_ratio + "x",
        annualized_turnover: i.annualized_turnover + "x",
        days_to_sell: i.days_to_sell >= 999 ? "999+" : i.days_to_sell,
      });
    });
  }
  const body =
    generateHeader("INVENTORY TURNOVER REPORT") +
    generateFiltersBar(filters) +
    summary +
    secondSummary +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Inventory Turnover Report", body);
};

const generateSupplierHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Suppliers", value: report.total_supplier_count },
    {
      label: "Total Revenue",
      value: formatCurrency(report.grand_total_revenue),
    },
    {
      label: "Total Inventory Cost",
      value: formatCurrency(report.grand_total_inventory_cost),
    },
    {
      label: "Total Returns",
      value: formatNumber(report.grand_total_returned),
    },
  ]);
  const periodInfo = report.from_date
    ? `<div class="filters-bar"><strong>Period:</strong> ${report.from_date} to ${report.to_date}</div>`
    : "";
  const headers = [
    { text: "Supplier", key: "supplier" },
    { text: "Items", key: "total_items", align: "center" },
    { text: "Stock", key: "total_stock_on_hand", align: "center" },
    { text: "Inv. Cost", key: "total_inventory_cost", align: "right" },
    { text: "Qty Sold", key: "total_qty_sold", align: "center" },
    { text: "Revenue", key: "total_revenue", align: "right" },
    { text: "Gross Profit", key: "gross_profit", align: "right" },
    { text: "Margin %", key: "avg_margin_pct", align: "center" },
    { text: "Returns", key: "total_returned", align: "center" },
    { text: "Return Rate", key: "return_rate_pct", align: "center" },
    { text: "Out of Stock", key: "out_of_stock_count", align: "center" },
    { text: "Low Stock", key: "low_stock_count", align: "center" },
    { text: "Categories", key: "item_types_str" },
  ];
  const sortedSuppliers = sortItems(
    report.suppliers || [],
    filters.sort_by === "item_name" ? "supplier" : filters.sort_by,
    filters.sort_dir
  );
  const rows = sortedSuppliers.map((s) => ({
    ...s,
    total_inventory_cost: formatCurrency(s.total_inventory_cost),
    total_revenue: formatCurrency(s.total_revenue),
    gross_profit: formatCurrency(s.gross_profit),
    avg_margin_pct: s.avg_margin_pct + "%",
    return_rate_pct: s.return_rate_pct + "%",
    item_types_str: (s.item_types || []).join(", "),
  }));
  const body =
    generateHeader("SUPPLIER PERFORMANCE REPORT") +
    generateFiltersBar(filters) +
    summary +
    periodInfo +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Supplier Performance Report", body);
};

// ─── Dispatcher ──────────────────────────────────────────────────────────

const generateStockPositionHTML = (report, filters) => {
  const gt = report.grand_totals || {};
  const summary = generateSummaryCards([
    { label: "Total Categories", value: gt.total_categories },
    { label: "Total Items", value: gt.total_items },
    { label: "Total Stock Qty", value: formatNumber(gt.total_quantity) },
    { label: "Total Cost Value", value: formatCurrency(gt.total_cost_value) },
    {
      label: "Total Retail Value",
      value: formatCurrency(gt.total_retail_value),
    },
    {
      label: "Potential Profit",
      value: formatCurrency(gt.total_potential_profit),
    },
  ]);

  // --- Section 1: Category Overview Table ---
  const overviewHeaders = [
    { text: "Category", key: "category" },
    { text: "Items", key: "item_count", align: "center" },
    { text: "Total Qty", key: "total_quantity", align: "center" },
    { text: "Cost Value", key: "cost_value", align: "right" },
    { text: "Retail Value", key: "retail_value", align: "right" },
    { text: "Potential Profit", key: "potential_profit", align: "right" },
    { text: "Avg Cost/Unit", key: "avg_cost_per_unit", align: "right" },
    { text: "Out of Stock", key: "out_of_stock_count", align: "center" },
    { text: "Low Stock", key: "low_stock_count", align: "center" },
  ];
  const overviewRows = (report.categories || []).map((c) => ({
    ...c,
    cost_value: formatCurrency(c.cost_value),
    retail_value: formatCurrency(c.retail_value),
    potential_profit: formatCurrency(c.potential_profit),
    avg_cost_per_unit: formatCurrency(c.avg_cost_per_unit),
  }));
  const overviewSection =
    `<h3 style="margin:10px 0 4px;color:#1565c0;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Category Overview</h3>` +
    generateTable(overviewHeaders, overviewRows);

  // --- Section 2: Detailed Breakdown per Category ---
  const itemHeaders = [
    { text: "Item Name", key: "item_name" },
    { text: "Supplier", key: "supplier" },
    { text: "Qty", key: "quantity", align: "center" },
    { text: "Unit Cost", key: "unit_price", align: "right" },
    { text: "Selling Price", key: "selling_price", align: "right" },
    { text: "Cost Value", key: "cost_value", align: "right" },
    { text: "Retail Value", key: "retail_value", align: "right" },
    { text: "Profit", key: "profit", align: "right" },
  ];

  const formatItem = (item) => ({
    ...item,
    supplier: item.supplier || "-",
    unit_price: formatCurrency(item.unit_price),
    selling_price: formatCurrency(item.selling_price),
    cost_value: formatCurrency(item.cost_value),
    retail_value: formatCurrency(item.retail_value),
    profit: formatCurrency(item.profit),
  });

  let detailHTML = `<h3 style="margin:16px 0 0;color:#1565c0;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Detailed Breakdown</h3>`;

  (report.categories || []).forEach((cat) => {
    // Category banner
    detailHTML += `<div class="category-banner">
      <span>${cat.category}</span>
      <div class="category-banner-sub">
        <span>${cat.item_count} items</span>
        <span>Qty: ${formatNumber(cat.total_quantity)}</span>
        <span>Cost: ${formatCurrency(cat.cost_value)}</span>
        <span>Retail: ${formatCurrency(cat.retail_value)}</span>
        <span>Profit: ${formatCurrency(cat.potential_profit)}</span>
        ${
          cat.out_of_stock_count > 0
            ? `<span style="color:#ffcdd2;">${cat.out_of_stock_count} OOS</span>`
            : ""
        }
        ${
          cat.low_stock_count > 0
            ? `<span style="color:#ffe0b2;">${cat.low_stock_count} Low</span>`
            : ""
        }
      </div>
    </div>`;

    const hasSubTypes = cat.sub_types && cat.sub_types.length > 0;
    const hasUncategorized =
      cat.uncategorized_items && cat.uncategorized_items.length > 0;

    // Build rows with sub-type grouping
    const rows = [];

    if (hasSubTypes) {
      cat.sub_types.forEach((st) => {
        rows.push({
          _isSubTypeHeader: true,
          _subTypeLabel: st.sub_type,
          _subTypeStats: `${st.item_count} items | Qty: ${
            st.total_quantity
          } | Cost: ${formatCurrency(st.cost_value)} | Profit: ${formatCurrency(
            st.potential_profit
          )}`,
        });
        (st.items || []).forEach((item) => rows.push(formatItem(item)));
      });
    }

    if (hasUncategorized) {
      if (hasSubTypes) {
        rows.push({
          _isSubTypeHeader: true,
          _subTypeLabel: "No Sub-Type",
          _subTypeStats: `${cat.uncategorized_items.length} items`,
        });
      }
      cat.uncategorized_items.forEach((item) => rows.push(formatItem(item)));
    }

    // Build table manually to support sub-type header styling
    if (rows.length === 0) {
      detailHTML += `<p style="text-align:center;color:#999;padding:8px;font-size:9px;">No items.</p>`;
    } else {
      detailHTML += `<table class="report-table"><thead><tr>`;
      itemHeaders.forEach((h) => {
        const align = h.align ? ` style="text-align:${h.align};"` : "";
        detailHTML += `<th${align}>${h.text}</th>`;
      });
      detailHTML += `</tr></thead><tbody>`;
      rows.forEach((row) => {
        if (row._isSubTypeHeader) {
          detailHTML += `<tr class="sub-type-header"><td colspan="${itemHeaders.length}"><span style="margin-right:10px;">${row._subTypeLabel}</span><span style="font-weight:400;font-size:8px;color:#666;">${row._subTypeStats}</span></td></tr>`;
        } else {
          detailHTML += `<tr>`;
          itemHeaders.forEach((h) => {
            const align = h.align ? ` style="text-align:${h.align};"` : "";
            detailHTML += `<td${align}>${
              row[h.key] !== undefined && row[h.key] !== null ? row[h.key] : "-"
            }</td>`;
          });
          detailHTML += `</tr>`;
        }
      });
      detailHTML += `</tbody></table>`;
    }
  });

  const body =
    generateHeader("STOCK POSITION BY CATEGORY") +
    generateFiltersBar(filters) +
    summary +
    overviewSection +
    detailHTML +
    generateFooter();
  return wrapHTML("Stock Position Report", body);
};

const generators = {
  low_stock: generateLowStockHTML,
  out_of_stock: generateOutOfStockHTML,
  fastest_moving: generateFastestMovingHTML,
  slow_moving: generateSlowMovingHTML,
  stock_movement: generateStockMovementHTML,
  turnover: generateTurnoverHTML,
  supplier: generateSupplierHTML,
  stock_position: generateStockPositionHTML,
};

const printReport = (html) => {
  const printWindow = window.open("", "", "width=1100,height=700");
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

export const printInventoryReport = (reportKey, reportData, filters) => {
  const generator = generators[reportKey];
  if (!generator) {
    showAlert({
      title: "Error",
      message: "Unknown report type: " + reportKey,
      color: "error",
      icon: "mdi-alert-circle",
    });
    return;
  }
  const html = generator(reportData, filters || {});
  printReport(html);
};
